import { MUSIC_MAX_SECONDS, parseMusicPreferences, type MusicPreferences } from './policy';

export const MUSIC_HANDOFF_KEY = 'untangled-music-handoff-v1';
export const MUSIC_HANDOFF_TTL = 120_000;
export type MusicHandoff = MusicPreferences & {
  schema: 1; savedAt: number; documentId: string; departing: boolean;
  trackId: string; version: string; position: number; resume: boolean;
};

export function parseMusicHandoff(raw: string | null, now = Date.now()): MusicHandoff | null {
  try {
    const value = JSON.parse(raw || 'null');
    if (!value || value.schema !== 1 || !Number.isFinite(value.savedAt) || now < value.savedAt || now - value.savedAt > MUSIC_HANDOFF_TTL || typeof value.documentId !== 'string' || typeof value.resume !== 'boolean' || typeof value.departing !== 'boolean') return null;
    const p = parseMusicPreferences(raw);
    if (!p.trackId || !p.version || p.position === undefined) return null;
    return { ...p, schema: 1, savedAt: value.savedAt, documentId: value.documentId, departing: value.departing, trackId: p.trackId, version: p.version, position: p.position, resume: value.resume };
  } catch { return null; }
}

export function continuationAllowed(value: MusicHandoff, documentId: string, navigation: string, sameOriginReferrer: boolean, hasOpener: boolean) {
  if (hasOpener) return false;
  // A component remount in this document is also a continuation. A checkpoint
  // copied to a new tab is not. Durable local preferences never imply intent.
  return value.documentId === documentId || (value.departing && (navigation === 'reload' || navigation === 'back_forward' || sameOriginReferrer));
}

export function recoveryPosition(position: number, duration: number) {
  const valid = Number.isFinite(position) ? Math.max(0, Math.min(position, MUSIC_MAX_SECONDS)) : 0;
  return Number.isFinite(duration) && duration > 0 ? Math.min(valid, Math.max(0, duration - .05)) : valid;
}

/** Never start audible playback before the saved seek has actually settled. */
export async function restoreMusicPosition(el: HTMLAudioElement, position: number, current: () => boolean, timeout = 12_000) {
  const wait = (ready: () => boolean) => new Promise<void>((resolve, reject) => {
    const events = ['loadedmetadata', 'durationchange', 'progress', 'canplay', 'seeked', 'error', 'abort'];
    const cleanup = () => { clearTimeout(timer); events.forEach(e => el.removeEventListener(e, check)); };
    const check = () => {
      if (!current()) { cleanup(); reject(new Error('Playback changed.')); }
      else if (el.error) { cleanup(); reject(new Error(`Media error ${el.error.code}`)); }
      else if (ready()) { cleanup(); resolve(); }
    };
    const timer = setTimeout(() => { cleanup(); reject(new Error('Media was not ready.')); }, timeout);
    events.forEach(e => el.addEventListener(e, check)); check();
  });
  await wait(() => el.readyState >= 1);
  const target = recoveryPosition(position, el.duration);
  if (target > 0 || el.currentTime > 0) {
    try { el.currentTime = target; }
    catch {
      // WebKit can expose metadata before the stream accepts a seek. Give its
      // real buffered/seekable state one bounded opportunity, then retain retry.
      await wait(() => el.seekable.length > 0 || el.readyState >= 2);
      el.currentTime = target;
    }
    await wait(() => !el.seeking && Math.abs(el.currentTime - target) < .3);
  }
  return target;
}
